import { RequestStaffPasswordResetInput } from "@cleanbudget/contract/session";
import { Alert, AlertDescription } from "@cleanbudget/ui/components/alert";
import { Button } from "@cleanbudget/ui/components/button";
import { IconMailCheck } from "@tabler/icons-react";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute, useLocation } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { match } from "ts-pattern";
import { AuthLayout } from "@/components/auth-layout";
import { ButtonLink } from "@/components/button-link";
import { Form, FormError, SubmitButton, TextField, useZodForm } from "@/components/form/form";
import { BotCheck } from "@/components/sign-in/bot-check";
import { NewPasswordFields } from "@/components/sign-in/new-password";
import { apiErrorTag } from "@/lib/api-error";
import { orpc } from "@/lib/orpc";

/**
 * "Wachtwoord vergeten" (ADR 0003): without a token, ask for the link by mail; with the link's
 * token in the fragment (`/password-reset#<token>`, out of every server log), choose the new
 * password. After that every session and trusted browser has ended, so the employee logs in again.
 */
export const Route = createFileRoute("/password-reset")({
	component: PasswordResetPage,
});

function PasswordResetPage() {
	const hash = useLocation({ select: (location) => location.hash.replace(/^#/, "") });
	// Kept once read: after saving, the used token leaves the address bar, and the page stays.
	const [token, setToken] = useState(hash);
	useEffect(() => {
		if (hash !== "") setToken(hash);
	}, [hash]);
	return token === "" ? <RequestLink /> : <ChoosePassword token={token} onRequestNew={() => setToken("")} />;
}

const RequestForm = RequestStaffPasswordResetInput.pick({ email: true });

function RequestLink() {
	const { t } = useTranslation("session");
	const { t: tCommon } = useTranslation();
	const form = useZodForm(RequestForm, { defaultValues: { email: "" } });
	const request = useMutation(orpc.session.requestPasswordReset.mutationOptions());
	const [turnstileToken, setTurnstileToken] = useState("");
	const [recheck, setRecheck] = useState(0);
	const [sent, setSent] = useState(false);

	const onSubmit = async (values: { email: string }) => {
		if (turnstileToken === "") {
			form.setError("root", { type: "server", message: t("botCheck.required") });
			return;
		}
		try {
			await request.mutateAsync({ ...values, turnstileToken });
			setSent(true);
		} catch (error) {
			const message = match(apiErrorTag(error))
				.with("BotCheckFailed", () => t("botCheck.failed"))
				.with("BotCheckUnavailable", () => t("botCheck.unavailable"))
				.with("RateLimited", () => t("problem.tooMany"))
				.otherwise(() => tCommon("form.failed"));
			form.setError("root", { type: "server", message });
			setRecheck((n) => n + 1);
		}
	};

	return (
		<AuthLayout title={t("reset.title")} description={sent ? undefined : t("reset.description")}>
			{sent ? (
				<Alert>
					<IconMailCheck aria-hidden />
					<AlertDescription>{t("reset.sent")}</AlertDescription>
				</Alert>
			) : (
				<Form form={form} onSubmit={onSubmit}>
					<TextField
						control={form.control}
						name="email"
						type="email"
						autoComplete="username"
						label={t("login.email")}
					/>
					<BotCheck onToken={setTurnstileToken} recheck={recheck} />
					<FormError control={form.control} />
					<SubmitButton control={form.control} size="lg">
						{t("reset.submit")}
					</SubmitButton>
				</Form>
			)}
			<ButtonLink to="/login" search={{ method: "password" }} variant="ghost">
				{t("reset.backToLogin")}
			</ButtonLink>
		</AuthLayout>
	);
}

function ChoosePassword({ token, onRequestNew }: { token: string; onRequestNew: () => void }) {
	const { t } = useTranslation("session");
	const { t: tCommon } = useTranslation();
	const reset = useMutation(orpc.session.resetPassword.mutationOptions());
	const [outcome, setOutcome] = useState<"done" | "invalid">();

	if (outcome !== undefined) {
		return (
			<AuthLayout title={t("reset.newTitle")}>
				<Alert variant={outcome === "done" ? "default" : "destructive"}>
					<AlertDescription>{outcome === "done" ? t("reset.done") : t("reset.invalid")}</AlertDescription>
				</Alert>
				{outcome === "done" ? (
					<ButtonLink to="/login" search={{ method: "password" }} size="lg">
						{t("login.submit")}
					</ButtonLink>
				) : (
					<Button size="lg" onClick={onRequestNew}>
						{t("reset.title")}
					</Button>
				)}
			</AuthLayout>
		);
	}
	return (
		<AuthLayout title={t("reset.newTitle")} description={t("reset.newDescription")}>
			<NewPasswordFields
				submitLabel={t("reset.save")}
				otherError={(error) =>
					apiErrorTag(error) === "RateLimited" ? t("problem.tooMany") : tCommon("form.failed")
				}
				onSave={async (password) => {
					try {
						await reset.mutateAsync({ token, password });
						setOutcome("done");
						// The used link out of the address bar and the history.
						window.history.replaceState(null, "", window.location.pathname);
					} catch (error) {
						if (apiErrorTag(error) === "PasswordResetLinkInvalid") {
							setOutcome("invalid");
							window.history.replaceState(null, "", window.location.pathname);
						} else throw error;
					}
				}}
			/>
		</AuthLayout>
	);
}
